import React, { useEffect, useState } from 'react';
import { Plug, Loader2, Copy, Check, RefreshCw, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminGetMcpInfo, adminRotateMcpToken, type McpInfo } from '../../features/mcp/api/mcp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const boxClass =
  'flex items-center gap-2 bg-[#050914] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white font-mono break-all';

export default function AdminMcpPage() {
  const [info, setInfo] = useState<McpInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState<string>('');

  useEffect(() => {
    adminGetMcpInfo()
      .then(setInfo)
      .catch((e: any) => toast.error('Gagal memuat', { description: e.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, []);

  const connectorUrl = info ? `${BACKEND_URL}${info.path}` : '';

  const copy = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success('Disalin ke clipboard');
    setTimeout(() => setCopied(''), 1500);
  };

  const rotate = async () => {
    if (!window.confirm('Rotasi token akan memutus koneksi Claude yang sudah ada. Anda harus memasang token baru di Claude. Lanjutkan?')) return;
    setRotating(true);
    try {
      const next = await adminRotateMcpToken();
      setInfo(next);
      setShowToken(true);
      toast.success('Token berhasil dirotasi');
    } catch (e: any) {
      toast.error('Gagal', { description: e.response?.data?.detail || e.message });
    } finally {
      setRotating(false);
    }
  };

  const maskedToken = info ? (showToken ? info.token : '•'.repeat(Math.min(info.token.length, 32))) : '';

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Konektor Claude (MCP)">
      <div className="max-w-3xl" data-testid="admin-mcp-page">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <Plug className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Integrasi AI</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Konektor Claude (MCP)</h2>
          </div>
        </div>

        {loading || !info ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : (
          <>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
              Hubungkan Claude ke platform Cuantrader memakai Model Context Protocol. Setelah terhubung, Anda bisa
              bertanya langsung ke Claude tentang member, VIP, course, pendapatan, signal, dan jurnal — bahkan membuat
              signal, memberi akses course, atau mengatur status VIP lewat percakapan.
            </p>

            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">URL Konektor (MCP Server)</label>
                <div className={boxClass}>
                  <span className="flex-1" data-testid="mcp-url">{connectorUrl}</span>
                  <button data-testid="mcp-copy-url" onClick={() => copy(connectorUrl, 'url')} className="shrink-0 text-cyan-400 hover:text-cyan-300 transition-colors">
                    {copied === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Token Rahasia (Authorization: Bearer)</label>
                <div className={boxClass}>
                  <span className="flex-1" data-testid="mcp-token">{maskedToken}</span>
                  <button data-testid="mcp-toggle-token" onClick={() => setShowToken((v) => !v)} className="shrink-0 text-white/50 hover:text-white transition-colors">
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button data-testid="mcp-copy-token" onClick={() => copy(info.token, 'token')} className="shrink-0 text-cyan-400 hover:text-cyan-300 transition-colors">
                    {copied === 'token' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-amber-400/80 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> Rahasiakan token ini. Siapa pun yang memilikinya bisa mengakses data platform Anda.
                </p>
              </div>

              <button
                data-testid="mcp-rotate-btn"
                onClick={rotate}
                disabled={rotating}
                className="bg-white/5 border border-white/10 text-white/80 hover:text-white hover:bg-white/10 font-medium px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {rotating ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <RefreshCw className="w-4 h-4" strokeWidth={2} />}
                Rotasi Token
              </button>
            </div>

            <div className="mt-6 bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <h3 className="text-base font-medium text-white mb-3">Cara Memasang di Claude</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-white/60 leading-relaxed">
                <li>Buka Claude → <span className="text-white/80">Settings → Connectors → Add custom connector</span>.</li>
                <li>Tempel <span className="text-white/80">URL Konektor</span> di atas sebagai alamat server MCP.</li>
                <li>Pada Authentication pilih <span className="text-white/80">No sign in / custom header</span>, lalu tambahkan request header:
                  <div className="mt-1.5 font-mono text-xs bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white/80">Authorization: Bearer &lt;token di atas&gt;</div>
                </li>
                <li>Simpan, lalu minta Claude menampilkan tools yang tersedia (mis. <span className="text-white/80">"ringkasan platform"</span>).</li>
              </ol>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
